import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Sun,
  Settings,
  FileText,
  Folder,
  ChevronLeft,
  ChevronRight,
  Landmark,
  Scale,
  Layout,
  LuBell,
} from '../../utils/icons';
import { useAuth } from '../../context/AuthContext';

interface SubNavItem {
  id: string;
  label: string;
  path: string;
  end?: boolean;
  icon: React.ComponentType<{ className?: string; size?: number }> | any;
}

const subNavItems: SubNavItem[] = [
  { id: 'appearance', label: 'Appearance', path: '/dashboard/settings/appearance', end: true, icon: Sun },
  { id: 'general', label: 'General', path: '/dashboard/settings/general', end: true, icon: Settings },
  { id: 'smtp', label: 'SMTP / Email', path: '/dashboard/settings/smtp', end: true, icon: Settings },
  { id: 'analytics', label: 'Analytics', path: '/dashboard/settings/analytics', end: true, icon: Layout },
  {
    id: 'notifications',
    label: 'Notifications',
    path: '/dashboard/settings/notifications',
    end: true,
    icon: LuBell,
  },
  { id: 'users', label: 'Users', path: '/dashboard/settings/users', icon: Layout },
  { id: 'roles', label: 'Roles', path: '/dashboard/settings/roles', icon: Layout },
  { id: 'case-types', label: 'Case Types', path: '/dashboard/settings/case-types', icon: FileText },
  { id: 'court-types', label: 'Courts', path: '/dashboard/settings/court-types', icon: Scale },
  { id: 'courts', label: 'Registries', path: '/dashboard/settings/courts', icon: Landmark },
  { id: 'departments', label: 'Departments', path: '/dashboard/settings/departments', icon: Folder },
  { id: 'judges', label: 'Judges', path: '/dashboard/settings/judges', icon: Layout },
];

const STORAGE_KEY = 'clicklaw.settingsSubSidebarCollapsed';

const SettingsLayout: React.FC = () => {
  const { user } = useAuth();
  const roles = (user as any)?.roles || [];
  const permissions = (user as any)?.permissions || [];
  const hasManageSettings =
    Array.isArray(permissions) && permissions.some((p: any) => p?.slug === 'manage_settings');
  const isAdminRole =
    Array.isArray(roles) && roles.some((r: any) => String(r?.slug).toLowerCase() === 'admin');
  const canAccessSettings = hasManageSettings || isAdminRole;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isHoverExpanded, setHoverExpanded] = useState(false);

  const canHover = useMemo(() => {
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch {
      return false;
    }
  }, []);

  const effectiveCollapsed = isCollapsed && !isHoverExpanded;

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, isCollapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [isCollapsed]);

  const ChevronLeftIcon = ChevronLeft as React.ComponentType<{ size?: number; className?: string }>;
  const ChevronRightIcon = ChevronRight as React.ComponentType<{ size?: number; className?: string }>;

  if (!canAccessSettings) {
    return (
      <main className="flex-1 min-w-0 p-6">
        <div className="max-w-2xl">
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
            Not authorized
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            You do not have permission to manage system settings. Please contact your administrator.
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="flex flex-1 min-w-0">
      <aside
        onMouseEnter={() => {
          if (!canHover) return;
          if (!isCollapsed) return;
          setHoverExpanded(true);
        }}
        onMouseLeave={() => {
          if (!canHover) return;
          setHoverExpanded(false);
        }}
        className={`flex-shrink-0 border-l border-gray-200 dark:border-gray-600 flex flex-col transition-all duration-200 ease-nav-brand ${
          effectiveCollapsed ? 'w-14 min-w-14' : 'w-52 min-w-52'
        } ${effectiveCollapsed ? 'pl-2 pr-2' : 'pl-4 pr-2'}`}
      >
        <div className="flex-none flex items-center justify-end py-3 pr-1">
          <button
            type="button"
            onClick={() => setIsCollapsed((v) => !v)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-gray-100/50 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-300 transition-colors"
            aria-label={isCollapsed ? 'Expand settings sidebar' : 'Collapse settings sidebar'}
            title={isCollapsed ? 'Expand settings sidebar' : 'Collapse settings sidebar'}
          >
            {isCollapsed ? (
              <ChevronRightIcon size={18} />
            ) : (
              <ChevronLeftIcon size={18} />
            )}
          </button>
        </div>
        <nav className="flex-1 min-h-0 py-2">
          <ul className="flex flex-col gap-0.5">
            {subNavItems.map((item) => (
              <li key={item.id}>
                <NavLink
                  to={item.path}
                  end={item.end ?? false}
                  title={item.label}
                  className={({ isActive }) =>
                    `flex items-center py-2 px-3 text-sm rounded-lg transition-colors ${
                      effectiveCollapsed ? 'justify-center' : ''
                    } ${
                      isActive
                        ? 'font-semibold text-slate-700 dark:text-slate-200 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${
                          isActive ? 'bg-primary-700' : 'bg-transparent'
                        } ${effectiveCollapsed ? '' : 'mr-3'}`}
                      >
                        <item.icon
                          className={`text-xs ${isActive ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`}
                          size={14}
                        />
                      </div>
                      <span
                        className={`whitespace-nowrap ${
                          effectiveCollapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100 min-w-0 truncate'
                        } transition-all duration-200`}
                      >
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
};

export default SettingsLayout;
